import type { HealthConnectPlugin, HealthRecordType } from './health-connect.js';

type SyncBatch = Awaited<ReturnType<HealthConnectPlugin['sync']>>['batches'][number];

export interface HealthConnectIngestResult {
  imported: number;
  updated?: number;
  deduplicated?: number;
  deleted?: number;
}

type SyncOptions = {
  bridge: Pick<HealthConnectPlugin, 'sync' | 'commitChanges'>;
  recordTypes: HealthRecordType[];
  since?: string;
  ingest: (batch: SyncBatch) => Promise<HealthConnectIngestResult>;
  finalize?: () => Promise<void>;
};
type SyncResult = { imported: number; updated: number; sourcePackages: string[] };
let activeSync: Promise<SyncResult> | null = null;

export function syncHealthConnect(options: SyncOptions): Promise<SyncResult> {
  if (activeSync) return activeSync;
  const task = performSync(options).finally(() => {
    if (activeSync === task) activeSync = null;
  });
  activeSync = task;
  return task;
}

async function performSync(options: SyncOptions): Promise<SyncResult> {
  const result = await options.bridge.sync({
    recordTypes: options.recordTypes,
    ...(options.since ? { since: options.since } : {}),
  });
  let imported = 0;
  let updated = 0;

  for (const batch of result.batches) {
    const response = await options.ingest(batch);
    imported += response.imported;
    updated += response.updated ?? 0;
    // Advance the device token only after the server has durably accepted this batch.
    // A failed upload is therefore retried safely on the next sync.
    await options.bridge.commitChanges({
      recordType: batch.recordType as HealthRecordType,
      token: batch.token,
    });
  }

  await options.finalize?.();

  return { imported, updated, sourcePackages: result.sourcePackages };
}
